import {
  AnalyticsEventName,
  DUPE_HOME_CATEGORY_SELECTOR_COOKIE_KEY,
} from '@dupe/common/constants'
import { ArmchairIcon, ShirtIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import HomeIGShareCTA from '~/src/components/home/HomeIGShareCTA'
import { Dialog, DialogContent } from '~/src/components/ui/dialog'
import useAnalytics from '~/src/hooks/useAnalytics'
import useDeviceDetect from '~/src/hooks/useDeviceDetect'
import useDismissible from '~/src/hooks/useDismissible'
import { cn } from '~/src/lib/utils'

const HomeCategorySelector = () => {
  const analytics = useAnalytics()
  const { isInstagram } = useDeviceDetect()
  const [open, setOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('')

  const { isDismissed, dismiss } = useDismissible(
    DUPE_HOME_CATEGORY_SELECTOR_COOKIE_KEY,
    false
  )

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.target as HTMLFormElement)
    const category = formData.get('category')
    const email = formData.get('email')
    const honeypot = !!email

    analytics.track(
      AnalyticsEventName.HomepageCategorySelected,
      {
        category,
        email,
        honeypot,
      },
      {
        sendToPlausible: true,
        sendToFb: true,
      }
    )

    setTimeout(() => {
      dismiss()
      setOpen(false)
    }, 350)
  }

  useEffect(() => {
    if (isDismissed) return
    if (!isInstagram) return

    const timer = setTimeout(() => {
      setOpen(true)
    }, 500)

    return () => clearTimeout(timer)
  }, [isDismissed, isInstagram])

  return (
    <>
      {isDismissed ? (
        <HomeIGShareCTA />
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            hideXClose
            onPointerDownOutside={(e) => {
              e.preventDefault()
            }}
            className='max-w-xs space-y-4 rounded-2xl outline-none'
          >
            <div>
              <h2 className='text-center text-xl font-semibold'>
                What are you duping?
              </h2>
            </div>
            <form onSubmit={handleSubmit}>
              <input type='text' name='email' style={{ display: 'none' }} />
              <input type='hidden' name='category' value={selectedCategory} />
              <div className='flex flex-col gap-6'>
                <div className='flex items-center justify-evenly gap-4'>
                  <CategoryButton
                    category='Furniture'
                    icon={<ArmchairIcon size={80} />}
                    selected={selectedCategory === 'Furniture'}
                    onClick={() => setSelectedCategory('Furniture')}
                  />
                  <div className='h-24 w-px bg-gray-100' />
                  <CategoryButton
                    category='Fashion'
                    icon={<ShirtIcon size={80} />}
                    selected={selectedCategory === 'Fashion'}
                    onClick={() => setSelectedCategory('Fashion')}
                  />
                </div>
                <div className='flex justify-center'>
                  <button
                    type='submit'
                    className='text-sm underline opacity-40'
                    name='category'
                    value=''
                  >
                    Neither, I&apos;m just browsing
                  </button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

const CategoryButton = ({
  category,
  icon,
  selected,
  onClick,
}: {
  category: string
  icon: React.ReactNode
  onClick: () => void
  selected: boolean
}) => {
  return (
    <button
      type='submit'
      name='category'
      value={category}
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-2 outline-none transition-colors',
        {
          'text-promo': selected,
        }
      )}
      onClick={onClick}
    >
      {icon}
      <span className='text-2xl font-bold'>{category}</span>
    </button>
  )
}

export default HomeCategorySelector
